import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const TournamentContext = createContext(null);
const CREATOR_KEY = 'bracketflow_creator_tokens';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
const generateToken = () => Math.random().toString(36).substr(2, 16);

// Calculate bracket rounds for winners bracket
const calculateRounds = (numParticipants) => {
  const totalSlots = Math.pow(2, Math.ceil(Math.log2(numParticipants)));
  const rounds = [];
  let matchesInRound = totalSlots / 2;
  let roundNum = 1;

  while (matchesInRound >= 1) {
    let roundName;
    if (matchesInRound === 1) roundName = 'Winners Final';
    else if (matchesInRound === 2) roundName = 'Winners Semis';
    else if (matchesInRound === 4) roundName = 'Winners Quarters';
    else roundName = `Winners R${roundNum}`;

    rounds.push({ name: roundName, matches: matchesInRound });
    if (matchesInRound === 1) break;
    matchesInRound = matchesInRound / 2;
    roundNum++;
  }

  return rounds;
};

// Calculate losers bracket rounds
const calculateLosersRounds = (numParticipants) => {
  const totalSlots = Math.pow(2, Math.ceil(Math.log2(numParticipants)));
  const winnersRounds = Math.ceil(Math.log2(totalSlots));
  const rounds = [];
  
  // Losers bracket has roughly 2x the rounds of winners (minus 1)
  // Each "level" has a round for losers dropping down + a round to halve
  let currentPlayers = totalSlots / 2; // First round losers
  let roundNum = 1;
  
  while (currentPlayers >= 1) {
    let roundName;
    if (currentPlayers === 1) roundName = 'Losers Final';
    else if (currentPlayers === 2) roundName = 'Losers Semis';
    else roundName = `Losers R${roundNum}`;
    
    rounds.push({ name: roundName, matches: Math.ceil(currentPlayers / 2) });
    
    if (currentPlayers === 1) break;
    currentPlayers = Math.ceil(currentPlayers / 2);
    roundNum++;
  }
  
  return rounds;
};

// Generate bracket matches (single elimination)
const generateBracketMatches = (participants) => {
  const numParticipants = participants.length;
  const totalSlots = Math.pow(2, Math.ceil(Math.log2(numParticipants)));
  const byes = totalSlots - numParticipants;
  const rounds = calculateRounds(numParticipants);
  const matches = [];

  // Shuffle participants first (not BYEs)
  const shuffledParticipants = [...participants];
  for (let i = shuffledParticipants.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledParticipants[i], shuffledParticipants[j]] = [shuffledParticipants[j], shuffledParticipants[i]];
  }

  // Create seeded array with proper BYE distribution
  const seeded = new Array(totalSlots).fill(null);
  
  // Place participants in alternating positions to spread out BYEs
  let participantIndex = 0;
  for (let i = 0; i < totalSlots && participantIndex < shuffledParticipants.length; i += 2) {
    seeded[i] = shuffledParticipants[participantIndex++];
  }
  for (let i = 1; i < totalSlots && participantIndex < shuffledParticipants.length; i += 2) {
    seeded[i] = shuffledParticipants[participantIndex++];
  }
  
  // Fill remaining slots with BYEs
  let byeIndex = 0;
  for (let i = 0; i < totalSlots; i++) {
    if (!seeded[i]) {
      seeded[i] = { id: `bye-${byeIndex}`, name: 'BYE', isBye: true };
      byeIndex++;
    }
  }

  // Generate first round
  let matchIdx = 0;
  for (let i = 0; i < seeded.length; i += 2) {
    const pA = seeded[i];
    const pB = seeded[i + 1];

    const match = {
      id: generateId(),
      round: 0,
      roundName: rounds[0]?.name || 'Round 1',
      matchIndex: matchIdx,
      participantA: pA,
      participantB: pB,
      scoreA: null,
      scoreB: null,
      winner: null,
      loser: null,
      status: 'pending',
      nextMatchIndex: Math.floor(matchIdx / 2),
      bracketType: 'winners',
    };

    if (pA?.isBye && pB?.isBye) {
      match.winner = null;
      match.loser = null;
      match.status = 'completed';
      match.scoreA = '-';
      match.scoreB = '-';
    } else if (pA?.isBye) {
      match.winner = pB;
      match.loser = pA;
      match.status = 'completed';
      match.scoreA = '-';
      match.scoreB = 'W';
    } else if (pB?.isBye) {
      match.winner = pA;
      match.loser = pB;
      match.status = 'completed';
      match.scoreA = 'W';
      match.scoreB = '-';
    }

    matches.push(match);
    matchIdx++;
  }

  // Generate subsequent rounds
  for (let roundIdx = 1; roundIdx < rounds.length; roundIdx++) {
    const matchesInRound = rounds[roundIdx].matches;
    for (let i = 0; i < matchesInRound; i++) {
      matches.push({
        id: generateId(),
        round: roundIdx,
        roundName: rounds[roundIdx].name,
        matchIndex: i,
        participantA: null,
        participantB: null,
        scoreA: null,
        scoreB: null,
        winner: null,
        loser: null,
        status: 'waiting',
        nextMatchIndex: roundIdx < rounds.length - 1 ? Math.floor(i / 2) : null,
        bracketType: 'winners',
      });
    }
  }

  // Process bye advancements
  for (let roundIdx = 0; roundIdx < rounds.length - 1; roundIdx++) {
    const currentMatches = matches.filter(m => m.round === roundIdx);
    const nextMatches = matches.filter(m => m.round === roundIdx + 1);

    currentMatches.forEach((match) => {
      if (match.winner && match.status === 'completed') {
        const nextMatch = nextMatches[match.nextMatchIndex];
        if (nextMatch) {
          const isFirst = match.matchIndex % 2 === 0;
          if (isFirst) nextMatch.participantA = match.winner;
          else nextMatch.participantB = match.winner;

          if (nextMatch.participantA && nextMatch.participantB) {
            nextMatch.status = 'pending';
          }
        }
      }
    });
  }

  return { matches, rounds };
};

// Generate double elimination bracket
// ==================== DOUBLE ELIMINATION ENGINE ====================
// Robust double-elimination engine supporting ANY participant count (>= 2),
// including odd numbers with byes. A bye = automatic advancement (no win, no
// loss). A participant is eliminated only after a second loss. Includes a
// Grand Final and a Grand Final Reset (bracket reset) match.
//
// The whole bracket state is derived deterministically from the set of
// user-entered results via recomputeDoubleElimination(), which also resolves
// byes and cascades any change (score edit / reset) correctly.

const nextPow2 = (n) => Math.pow(2, Math.ceil(Math.log2(Math.max(2, n))));

// Standard bracket seed order for a bracket of size `size` (spreads byes so a
// real player faces a bye, never bye-vs-bye at the top of the bracket).
const seedOrder = (size) => {
  let seeds = [1];
  while (seeds.length < size) {
    const sum = seeds.length * 2 + 1;
    const next = [];
    for (const s of seeds) { next.push(s); next.push(sum - s); }
    seeds = next;
  }
  return seeds;
};

const makeBye = (i) => ({ id: `bye-${i}-${Math.random().toString(36).slice(2, 7)}`, name: 'BYE', isBye: true });

const winnersRoundName = (idx, total) => {
  const fromEnd = total - 1 - idx;
  if (fromEnd === 0) return 'Winners Final';
  if (fromEnd === 1) return 'Winners Semis';
  if (fromEnd === 2) return 'Winners Quarters';
  return `Winners R${idx + 1}`;
};

// Recompute the full double-elimination state from the user's recorded results.
const recomputeDoubleElimination = (matchesIn, losersIn, grandFinalIn, grandFinalResetIn) => {
  const matches = (matchesIn || []).map((m) => ({ ...m }));
  const losers = (losersIn || []).map((m) => ({ ...m }));
  const grandFinal = grandFinalIn ? { ...grandFinalIn } : null;
  const grandFinalReset = grandFinalResetIn ? { ...grandFinalResetIn } : null;

  const byId = {};
  for (const m of matches) byId[m.id] = m;
  for (const m of losers) byId[m.id] = m;

  // Capture user decisions (only real, scored matches — byes are re-derived).
  const decisions = {};
  const capture = (m) => {
    if (m && typeof m.scoreA === 'number' && typeof m.scoreB === 'number' && m.winner) {
      decisions[m.id] = { winnerId: m.winner.id, scoreA: m.scoreA, scoreB: m.scoreB };
    }
  };
  matches.forEach(capture);
  losers.forEach(capture);
  capture(grandFinal);
  capture(grandFinalReset);

  // Clear derived state (keep WB round-0 seeds).
  for (const m of matches) {
    if (m.round !== 0) { m.participantA = null; m.participantB = null; }
    m.winner = null; m.loser = null; m.scoreA = null; m.scoreB = null;
    m.status = m.round === 0 ? 'pending' : 'waiting';
  }
  for (const m of losers) {
    m.participantA = null; m.participantB = null;
    m.winner = null; m.loser = null; m.scoreA = null; m.scoreB = null;
    m.status = 'waiting';
  }
  for (const gf of [grandFinal, grandFinalReset]) {
    if (!gf) continue;
    gf.participantA = null; gf.participantB = null;
    gf.winner = null; gf.loser = null; gf.scoreA = null; gf.scoreB = null;
    gf.status = 'waiting';
  }

  const setSlot = (target, participant) => {
    if (!target) return;
    let t;
    if (target.target === 'GF') {
      t = grandFinal && target.matchId === grandFinal.id ? grandFinal : byId[target.matchId];
    } else {
      t = byId[target.matchId];
    }
    if (!t) return;
    if (target.slot === 'A') t.participantA = participant;
    else t.participantB = participant;
  };

  // Fixpoint resolution of the winners + losers brackets.
  const all = [...matches, ...losers];
  let changed = true;
  let guard = 0;
  while (changed && guard < 2000) {
    changed = false;
    guard++;
    for (const m of all) {
      if (m.status === 'completed') continue;
      const a = m.participantA;
      const b = m.participantB;
      if (a && b) {
        if (a.isBye && b.isBye) {
          m.winner = a; m.loser = b; m.status = 'completed';
          setSlot(m.winnerTo, a); setSlot(m.loserTo, a);
          changed = true;
        } else if (a.isBye) {
          m.winner = b; m.loser = a; m.status = 'completed';
          setSlot(m.winnerTo, b); setSlot(m.loserTo, a);
          changed = true;
        } else if (b.isBye) {
          m.winner = a; m.loser = b; m.status = 'completed';
          setSlot(m.winnerTo, a); setSlot(m.loserTo, b);
          changed = true;
        } else {
          const dec = decisions[m.id];
          if (dec && (dec.winnerId === a.id || dec.winnerId === b.id)) {
            const w = dec.winnerId === a.id ? a : b;
            const l = dec.winnerId === a.id ? b : a;
            m.winner = w; m.loser = l; m.scoreA = dec.scoreA; m.scoreB = dec.scoreB; m.status = 'completed';
            setSlot(m.winnerTo, w); setSlot(m.loserTo, l);
            changed = true;
          } else if (m.status !== 'pending') {
            m.status = 'pending';
            changed = true;
          }
        }
      } else if (m.status !== 'waiting') {
        m.status = 'waiting';
        changed = true;
      }
    }
  }

  // Grand Final + Bracket Reset.
  let champion = null;
  let runnerUp = null;
  let status = 'active';
  if (grandFinal) {
    const a = grandFinal.participantA;
    const b = grandFinal.participantB;
    if (a && b && !a.isBye && !b.isBye) {
      const dec = decisions[grandFinal.id];
      if (dec && (dec.winnerId === a.id || dec.winnerId === b.id)) {
        const w = dec.winnerId === a.id ? a : b;
        const l = dec.winnerId === a.id ? b : a;
        grandFinal.winner = w; grandFinal.loser = l;
        grandFinal.scoreA = dec.scoreA; grandFinal.scoreB = dec.scoreB; grandFinal.status = 'completed';
        if (w.id === a.id) {
          // Winners-bracket finalist (0 losses) wins -> champion immediately.
          champion = w; runnerUp = l; status = 'completed';
        } else if (grandFinalReset) {
          // Losers-bracket finalist wins -> both now have 1 loss -> reset match.
          grandFinalReset.participantA = a;
          grandFinalReset.participantB = b;
          const rdec = decisions[grandFinalReset.id];
          if (rdec && (rdec.winnerId === a.id || rdec.winnerId === b.id)) {
            const rw = rdec.winnerId === a.id ? a : b;
            const rl = rdec.winnerId === a.id ? b : a;
            grandFinalReset.winner = rw; grandFinalReset.loser = rl;
            grandFinalReset.scoreA = rdec.scoreA; grandFinalReset.scoreB = rdec.scoreB;
            grandFinalReset.status = 'completed';
            champion = rw; runnerUp = rl; status = 'completed';
          } else {
            grandFinalReset.status = 'pending';
          }
        }
      } else {
        grandFinal.status = 'pending';
      }
    }
  }

  return { matches, losersMatches: losers, grandFinal, grandFinalReset, champion, runnerUp, status };
};

const generateDoubleEliminationBracket = (participants) => {
  const N = participants.length;
  const S = nextPow2(N);
  const W = Math.round(Math.log2(S)); // number of winners rounds

  // Seed winners round 0 with byes spread out.
  const order = seedOrder(S);
  const slots = order.map((seed) => (seed <= N ? participants[seed - 1] : null));
  let byeCounter = 0;
  for (let i = 0; i < slots.length; i++) {
    if (!slots[i]) slots[i] = makeBye(byeCounter++);
  }

  const winnersRounds = [];
  for (let r = 0; r < W; r++) {
    winnersRounds.push({ name: winnersRoundName(r, W), matches: S / Math.pow(2, r + 1) });
  }

  const wb = [];
  const wbByRound = [];
  for (let r = 0; r < W; r++) {
    const count = winnersRounds[r].matches;
    const arr = [];
    for (let i = 0; i < count; i++) {
      const m = {
        id: generateId(),
        bracket: 'winners',
        bracketType: 'winners',
        round: r,
        roundName: winnersRounds[r].name,
        matchIndex: i,
        participantA: null, participantB: null,
        scoreA: null, scoreB: null, winner: null, loser: null,
        status: r === 0 ? 'pending' : 'waiting',
        nextMatchIndex: r < W - 1 ? Math.floor(i / 2) : null,
        winnerTo: null, loserTo: null,
      };
      arr.push(m); wb.push(m);
    }
    wbByRound.push(arr);
  }
  for (let i = 0; i < wbByRound[0].length; i++) {
    wbByRound[0][i].participantA = slots[2 * i];
    wbByRound[0][i].participantB = slots[2 * i + 1];
  }
  for (let r = 0; r < W - 1; r++) {
    for (let i = 0; i < wbByRound[r].length; i++) {
      const target = wbByRound[r + 1][Math.floor(i / 2)];
      wbByRound[r][i].winnerTo = { matchId: target.id, slot: i % 2 === 0 ? 'A' : 'B' };
    }
  }

  // Losers bracket structure.
  const lbRoundsMeta = [];
  if (W >= 2) {
    lbRoundsMeta.push({ matches: S / 4, type: 'lb1' });
    for (let r = 2; r <= W; r++) {
      lbRoundsMeta.push({ matches: S / Math.pow(2, r), type: 'major', wbRound: r - 1 });
      if (r < W) lbRoundsMeta.push({ matches: S / Math.pow(2, r + 1), type: 'minor' });
    }
  }

  const lb = [];
  const lbByRound = [];
  const lbTotal = lbRoundsMeta.length;
  for (let r = 0; r < lbTotal; r++) {
    const meta = lbRoundsMeta[r];
    const isFinal = r === lbTotal - 1;
    const arr = [];
    for (let i = 0; i < meta.matches; i++) {
      const m = {
        id: generateId(),
        bracket: 'losers',
        bracketType: 'losers',
        round: r,
        roundName: isFinal ? 'Losers Final' : `Losers R${r + 1}`,
        matchIndex: i,
        participantA: null, participantB: null,
        scoreA: null, scoreB: null, winner: null, loser: null,
        status: 'waiting',
        nextMatchIndex: isFinal ? null : Math.floor(i / 2),
        winnerTo: null, loserTo: null,
      };
      arr.push(m); lb.push(m);
    }
    lbByRound.push(arr);
  }

  // Wire losers-bracket winner advancement.
  for (let r = 0; r < lbTotal - 1; r++) {
    const meta = lbRoundsMeta[r];
    const arr = lbByRound[r];
    const nextArr = lbByRound[r + 1];
    if (meta.type === 'lb1' || meta.type === 'minor') {
      for (let i = 0; i < arr.length; i++) {
        arr[i].winnerTo = { matchId: nextArr[i].id, slot: 'A' };
      }
    } else if (meta.type === 'major') {
      for (let i = 0; i < arr.length; i++) {
        arr[i].winnerTo = { matchId: nextArr[Math.floor(i / 2)].id, slot: i % 2 === 0 ? 'A' : 'B' };
      }
    }
  }

  // Wire winners-bracket losers dropping into the losers bracket.
  if (lbTotal > 0) {
    for (let i = 0; i < wbByRound[0].length; i++) {
      const target = lbByRound[0][Math.floor(i / 2)];
      wbByRound[0][i].loserTo = { matchId: target.id, slot: i % 2 === 0 ? 'A' : 'B' };
    }
    for (let r = 0; r < lbTotal; r++) {
      const meta = lbRoundsMeta[r];
      if (meta.type === 'major') {
        const wr = meta.wbRound;
        const arr = lbByRound[r];
        for (let i = 0; i < wbByRound[wr].length; i++) {
          wbByRound[wr][i].loserTo = { matchId: arr[i].id, slot: 'B' };
        }
      }
    }
  }

  const grandFinal = {
    id: generateId(), bracket: 'grandFinal', bracketType: 'grandFinal',
    round: 9000, roundName: 'Grand Final', matchIndex: 0,
    participantA: null, participantB: null,
    scoreA: null, scoreB: null, winner: null, loser: null,
    status: 'waiting', nextMatchIndex: null, winnerTo: null, loserTo: null,
  };
  const grandFinalReset = {
    id: generateId(), bracket: 'grandFinalReset', bracketType: 'grandFinalReset',
    round: 9001, roundName: 'Grand Final Reset', matchIndex: 0,
    participantA: null, participantB: null,
    scoreA: null, scoreB: null, winner: null, loser: null,
    status: 'waiting', nextMatchIndex: null, winnerTo: null, loserTo: null,
  };

  // WB final winner -> Grand Final slot A.
  wbByRound[W - 1][0].winnerTo = { matchId: grandFinal.id, slot: 'A', target: 'GF' };
  if (lbTotal > 0) {
    lbByRound[lbTotal - 1][0].winnerTo = { matchId: grandFinal.id, slot: 'B', target: 'GF' };
  } else {
    // N === 2: the WB final loser is the losers-bracket finalist.
    wbByRound[W - 1][0].loserTo = { matchId: grandFinal.id, slot: 'B', target: 'GF' };
  }

  // Resolve byes and produce the initial state.
  const out = recomputeDoubleElimination(wb, lb, grandFinal, grandFinalReset);

  return {
    matches: out.matches,
    rounds: winnersRounds,
    losersMatches: out.losersMatches,
    losersRounds: lbRoundsMeta.map((meta, r) => ({
      name: r === lbTotal - 1 ? 'Losers Final' : `Losers R${r + 1}`,
      matches: meta.matches,
    })),
    grandFinal: out.grandFinal,
    grandFinalReset: out.grandFinalReset,
  };
};

// Generate bracket with bronze medal match (3rd place playoff)
const generateBracketWithBronzeMatch = (participants) => {
  // First generate normal single elimination bracket
  const { matches, rounds } = generateBracketMatches(participants);
  
  // Create bronze medal match (losers of semifinals play each other)
  const bronzeMatch = {
    id: generateId(),
    round: 998, // Special round number for bronze match
    roundName: 'Bronze Medal Match',
    matchIndex: 0,
    participantA: null, // Will be populated when semifinal completes
    participantB: null, // Will be populated when semifinal completes
    scoreA: null,
    scoreB: null,
    winner: null,
    loser: null,
    status: 'waiting',
    nextMatchIndex: null,
    bracketType: 'bronze',
  };
  
  return { matches, rounds, bronzeMatch };
};

// Generate league matches with balanced scheduling
const generateLeagueMatches = (teams) => {
  const matches = [];
  const n = teams.length;
  
  const teamList = [...teams];
  if (n % 2 !== 0) {
    teamList.push({ id: 'bye', name: 'BYE', isBye: true });
  }
  
  const numTeams = teamList.length;
  const numRounds = numTeams - 1;
  const matchesPerRound = numTeams / 2;
  
  for (let round = 0; round < numRounds; round++) {
    for (let match = 0; match < matchesPerRound; match++) {
      let home, away;
      
      if (match === 0) {
        home = teamList[0];
        const awayIdx = (round % (numTeams - 1)) + 1;
        away = teamList[awayIdx];
      } else {
        const homeIdx = (round + match) % (numTeams - 1) + 1;
        const awayIdx = (round + numTeams - 1 - match) % (numTeams - 1) + 1;
        home = teamList[homeIdx];
        away = teamList[awayIdx];
      }
      
      if (home?.isBye || away?.isBye) continue;
      
      matches.push({
        id: generateId(),
        round: round + 1,
        participantA: home,
        participantB: away,
        scoreA: null,
        scoreB: null,
        winner: null,
        status: 'pending',
        scheduledDate: '',
        scheduledTime: '',
        location: '',
        notes: '',
      });
    }
  }
  
  if (matches.length === 0) {
    for (let i = 0; i < teams.length; i++) {
      for (let j = i + 1; j < teams.length; j++) {
        matches.push({
          id: generateId(),
          round: Math.floor(matches.length / Math.floor(teams.length / 2)) + 1,
          participantA: teams[i],
          participantB: teams[j],
          scoreA: null,
          scoreB: null,
          winner: null,
          status: 'pending',
          scheduledDate: '',
          scheduledTime: '',
          location: '',
          notes: '',
        });
      }
    }
  }
  
  return matches;
};

// Calculate standings
const calculateStandings = (teams, matches) => {
  const standings = teams.map(team => ({
    ...team,
    played: 0, wins: 0, draws: 0, losses: 0,
    goalsFor: 0, goalsAgainst: 0, goalDiff: 0, points: 0,
  }));

  matches.forEach(match => {
    if (match.status === 'completed' && match.scoreA !== null && match.scoreB !== null) {
      const teamA = standings.find(t => t.id === match.participantA?.id);
      const teamB = standings.find(t => t.id === match.participantB?.id);

      if (teamA && teamB) {
        teamA.played++; teamB.played++;
        teamA.goalsFor += match.scoreA;
        teamA.goalsAgainst += match.scoreB;
        teamB.goalsFor += match.scoreB;
        teamB.goalsAgainst += match.scoreA;

        if (match.scoreA > match.scoreB) {
          teamA.wins++; teamA.points += 3; teamB.losses++;
        } else if (match.scoreB > match.scoreA) {
          teamB.wins++; teamB.points += 3; teamA.losses++;
        } else {
          teamA.draws++; teamB.draws++;
          teamA.points += 1; teamB.points += 1;
        }

        teamA.goalDiff = teamA.goalsFor - teamA.goalsAgainst;
        teamB.goalDiff = teamB.goalsFor - teamB.goalsAgainst;
      }
    }
  });

  return standings.sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.goalDiff !== a.goalDiff) return b.goalDiff - a.goalDiff;
    return b.goalsFor - a.goalsFor;
  });
};

// Roll back ALL propagation effects caused by a single match's result.
// Mutates the provided (shallow-copied) arrays/objects in place. Used by both
// resetMatch (remove a score) and updateBracketScore (re-edit a completed score)
// so that any team that advanced, dropped to the losers bracket, or went to the
// bronze match because of this result is removed and cascaded downstream.
const rollbackMatchEffects = (winners, losers, bronze, grandFinal, matchId, bracketType) => {
  const findMatch = (id) =>
    winners.find((m) => m.id === id) || losers.find((m) => m.id === id);

  const clearResult = (m) => {
    m.scoreA = null;
    m.scoreB = null;
    m.winner = null;
    m.loser = null;
    m.status = m.participantA && m.participantB ? 'pending' : 'waiting';
  };

  const removeFromGrandFinal = (team) => {
    if (!grandFinal || !team) return;
    let changed = false;
    if (grandFinal.participantA && grandFinal.participantA.id === team.id) {
      grandFinal.participantA = null;
      changed = true;
    }
    if (grandFinal.participantB && grandFinal.participantB.id === team.id) {
      grandFinal.participantB = null;
      changed = true;
    }
    if (changed) {
      grandFinal.scoreA = null;
      grandFinal.scoreB = null;
      grandFinal.winner = null;
      grandFinal.loser = null;
      grandFinal.status =
        grandFinal.participantA && grandFinal.participantB ? 'pending' : 'waiting';
    }
  };

  const removeFromBronze = (team) => {
    if (!bronze || !team) return;
    let changed = false;
    if (bronze.participantA && bronze.participantA.id === team.id) {
      bronze.participantA = null;
      changed = true;
    }
    if (bronze.participantB && bronze.participantB.id === team.id) {
      bronze.participantB = null;
      changed = true;
    }
    if (changed) {
      bronze.scoreA = null;
      bronze.scoreB = null;
      bronze.winner = null;
      bronze.loser = null;
      bronze.status = bronze.participantA && bronze.participantB ? 'pending' : 'waiting';
    }
  };

  // Remove `team` from a downstream match slot; if that match already had a
  // result, roll back its own effects first (recursive cascade).
  const removeFromMatch = (match, team, type) => {
    if (!match || !team) return;
    if (match.status === 'completed' && match.winner) {
      rollback(match, type);
    }
    if (match.participantA && match.participantA.id === team.id) match.participantA = null;
    else if (match.participantB && match.participantB.id === team.id) match.participantB = null;
    match.scoreA = null;
    match.scoreB = null;
    match.winner = null;
    match.loser = null;
    match.status = match.participantA && match.participantB ? 'pending' : 'waiting';
  };

  const rollback = (m, type) => {
    const w = m.winner;
    const l = m.loser;
    clearResult(m);

    // Undo the winner's advancement
    if (w) {
      if (type === 'winners') {
        if (m.roundName === 'Winners Final') {
          removeFromGrandFinal(w);
        } else if (m.nextMatchIndex !== null && m.nextMatchIndex !== undefined) {
          const next = winners.filter((x) => x.round === m.round + 1)[m.nextMatchIndex];
          if (next) removeFromMatch(next, w, 'winners');
        }
      } else {
        if (m.roundName === 'Losers Final') {
          removeFromGrandFinal(w);
        } else if (m.nextMatchIndex !== null && m.nextMatchIndex !== undefined) {
          const next = losers.filter((x) => x.round === m.round + 1)[m.nextMatchIndex];
          if (next) removeFromMatch(next, w, 'losers');
        }
      }
    }

    // Undo the loser's drop (only winners-bracket losers drop down / to bronze)
    if (l && type === 'winners') {
      const dropMatch = losers.find(
        (x) =>
          (x.participantA && x.participantA.id === l.id) ||
          (x.participantB && x.participantB.id === l.id)
      );
      if (dropMatch) removeFromMatch(dropMatch, l, 'losers');
      removeFromBronze(l);
    }
  };

  const target = findMatch(matchId);
  if (target) rollback(target, bracketType);
};

export function TournamentProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [currentTournament, setCurrentTournament] = useState(null);
  const [isCreator, setIsCreator] = useState(false);
  const [loading, setLoading] = useState(false);

  const getCreatorTokens = () => {
    try {
      const tokens = localStorage.getItem(CREATOR_KEY);
      return tokens ? JSON.parse(tokens) : {};
    } catch { return {}; }
  };

  const saveCreatorToken = (tournamentId, token) => {
    const tokens = getCreatorTokens();
    tokens[tournamentId] = token;
    localStorage.setItem(CREATOR_KEY, JSON.stringify(tokens));
  };

  const checkIsCreator = (tournamentId) => {
    const tokens = getCreatorTokens();
    return !!tokens[tournamentId];
  };

  const getCreatorToken = (tournamentId) => {
    const tokens = getCreatorTokens();
    return tokens[tournamentId] || null;
  };

  // Create tournament
  const createTournament = useCallback(async (config) => {
    const { name, code, sportType, format, participants, hasLosersLeague, hasBronzeMatch, hasPlayerStats } = config;

    // Handle both old format (string[]) and new format (object[]{name, logo, players})
    const participantObjects = participants.map((p, idx) => ({
      id: generateId(),
      name: typeof p === 'string' ? p : p.name,
      logo: typeof p === 'object' ? p.logo : null,
      players: (typeof p === 'object' && Array.isArray(p.players)) ? p.players : [],
      seed: idx + 1,
    }));

    const creatorToken = generateToken();

    let tournamentData = {
      name,
      code: code.toUpperCase(),
      creatorToken,
      sportType,
      format,
      participants: participantObjects,
      hasLosersLeague: format === 'bracket' && hasLosersLeague,
      hasBronzeMatch: format === 'bracket' && hasBronzeMatch,
      hasPlayerStats: !!hasPlayerStats,
      playerStats: {},
    };

    if (format === 'league') {
      tournamentData.matches = generateLeagueMatches(participantObjects);
      tournamentData.standings = calculateStandings(participantObjects, []);
    } else if (hasLosersLeague) {
      // Double elimination
      const { matches, rounds, losersMatches, losersRounds, grandFinal, grandFinalReset } = 
        generateDoubleEliminationBracket(participantObjects);
      tournamentData.matches = matches;
      tournamentData.rounds = rounds;
      tournamentData.losersMatches = losersMatches;
      tournamentData.losersRounds = losersRounds;
      tournamentData.grandFinal = grandFinal;
      tournamentData.grandFinalReset = grandFinalReset;
    } else if (hasBronzeMatch) {
      // Single elimination with bronze medal match
      const { matches, rounds, bronzeMatch } = generateBracketWithBronzeMatch(participantObjects);
      tournamentData.matches = matches;
      tournamentData.rounds = rounds;
      tournamentData.bronzeMatch = bronzeMatch;
    } else {
      // Single elimination
      const { matches, rounds } = generateBracketMatches(participantObjects);
      tournamentData.matches = matches;
      tournamentData.rounds = rounds;
    }

    try {
      const response = await fetch(`${API_URL}/api/tournaments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(tournamentData),
      });

      if (!response.ok) {
        throw new Error('Failed to create tournament');
      }

      const tournament = await response.json();
      saveCreatorToken(tournament.id, creatorToken);
      setIsCreator(true);
      setCurrentTournament(tournament);
      setTournaments(prev => [...prev, tournament]);
      
      return tournament;
    } catch (error) {
      console.error('Error creating tournament:', error.message);
      throw error;
    }
  }, []);

  // Edit an existing tournament.
  //  - rebuild=true  -> structural change (participants/format): regenerate the
  //    whole bracket/schedule and RESET all results.
  //  - rebuild=false -> metadata/rename only: update name/code/sport and propagate
  //    participant name/logo changes into existing matches WITHOUT losing scores.
  const editTournament = useCallback(async (tournamentId, config, rebuild) => {
    const creatorToken = getCreatorToken(tournamentId);
    const { name, code, sportType, format, participants, hasLosersLeague, hasBronzeMatch, hasPlayerStats } = config;

    const putEdit = async (payload) => {
      const response = await fetch(`${API_URL}/api/tournaments/${tournamentId}?creator_token=${creatorToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update tournament');
      }
      const updated = await response.json();
      setCurrentTournament(updated);
      setTournaments(prev => prev.map(x => (x.id === updated.id ? updated : x)));
      return updated;
    };

    if (rebuild) {
      const participantObjects = participants.map((p, idx) => ({
        id: p.id || generateId(),
        name: typeof p === 'string' ? p : p.name,
        logo: typeof p === 'object' ? p.logo : null,
        players: (typeof p === 'object' && Array.isArray(p.players)) ? p.players : [],
        seed: idx + 1,
      }));

      const payload = {
        name,
        code: code.toUpperCase(),
        sportType,
        format,
        participants: participantObjects,
        hasLosersLeague: format === 'bracket' && hasLosersLeague,
        hasBronzeMatch: format === 'bracket' && hasBronzeMatch,
        hasPlayerStats: !!hasPlayerStats,
        champion: null,
        status: 'active',
        losersMatches: [],
        losersRounds: [],
        grandFinal: null,
        bronzeMatch: null,
        standings: [],
        rounds: null,
        playerStats: {},
      };

      if (format === 'league') {
        payload.matches = generateLeagueMatches(participantObjects);
        payload.standings = calculateStandings(participantObjects, []);
      } else if (format === 'bracket' && hasLosersLeague) {
        const { matches, rounds, losersMatches, losersRounds, grandFinal, grandFinalReset } =
          generateDoubleEliminationBracket(participantObjects);
        payload.matches = matches;
        payload.rounds = rounds;
        payload.losersMatches = losersMatches;
        payload.losersRounds = losersRounds;
        payload.grandFinal = grandFinal;
        payload.grandFinalReset = grandFinalReset;
      } else if (format === 'bracket' && hasBronzeMatch) {
        const { matches, rounds, bronzeMatch } = generateBracketWithBronzeMatch(participantObjects);
        payload.matches = matches;
        payload.rounds = rounds;
        payload.bronzeMatch = bronzeMatch;
      } else {
        const { matches, rounds } = generateBracketMatches(participantObjects);
        payload.matches = matches;
        payload.rounds = rounds;
      }

      return await putEdit(payload);
    }

    // Non-destructive: metadata + rename propagation
    const map = {};
    participants.forEach(p => { if (p.id) map[p.id] = { name: p.name, logo: p.logo ?? null, players: Array.isArray(p.players) ? p.players : [] }; });
    const remap = (x) => (x && x.id && map[x.id]) ? { ...x, name: map[x.id].name, logo: map[x.id].logo, players: map[x.id].players } : x;
    const remapMatch = (m) => (m ? {
      ...m,
      participantA: remap(m.participantA),
      participantB: remap(m.participantB),
      winner: remap(m.winner),
      loser: remap(m.loser),
    } : m);

    const t = currentTournament || {};
    const payload = {
      name,
      code: code.toUpperCase(),
      sportType,
      hasPlayerStats: !!hasPlayerStats,
      participants: (t.participants || []).map(remap),
    };
    if (t.matches) payload.matches = t.matches.map(remapMatch);
    if (t.losersMatches) payload.losersMatches = t.losersMatches.map(remapMatch);
    if (t.bronzeMatch) payload.bronzeMatch = remapMatch(t.bronzeMatch);
    if (t.grandFinal) payload.grandFinal = remapMatch(t.grandFinal);
    if (t.standings) payload.standings = t.standings.map(remap);
    if (t.champion) payload.champion = remap(t.champion);

    return await putEdit(payload);
  }, [currentTournament]);


  // Load tournament by ID
  const loadTournament = useCallback(async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/tournaments/${id}`);
      if (!response.ok) throw new Error('Tournament not found');
      
      const tournament = await response.json();
      setCurrentTournament(tournament);
      setIsCreator(checkIsCreator(tournament.id) || (!!user && tournament.owner_user_id === user.user_id));
      
      return tournament;
    } catch (error) {
      console.error('Error loading tournament:', error);
      return null;
    }
  }, [user]);

  // Find tournament by code (name optional)
  const findTournamentByCode = useCallback(async (searchCode) => {
    try {
      const response = await fetch(`${API_URL}/api/tournaments/view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: (searchCode || '').toUpperCase() }),
      });

      if (!response.ok) return null;

      const tournament = await response.json();
      setCurrentTournament(tournament);
      setIsCreator(checkIsCreator(tournament.id) || (!!user && tournament.owner_user_id === user.user_id));
      
      return tournament;
    } catch (error) {
      console.error('Error finding tournament:', error);
      return null;
    }
  }, [user]);

  // Update bracket score (handles both single and double elimination)
  const updateBracketScore = useCallback(async (matchId, scoreA, scoreB, bracketType = 'winners', matchPlayerStats = null) => {
    if (!currentTournament || !isCreator) return;

    const creatorToken = getCreatorToken(currentTournament.id);

    // Merge this match's player stats into the tournament-level dict.
    const mergedStats = { ...(currentTournament.playerStats || {}) };
    if (matchPlayerStats) mergedStats[matchId] = matchPlayerStats;

    // Double elimination: route ALL score changes through the recompute engine.
    if (currentTournament.hasLosersLeague) {
      const wb = (currentTournament.matches || []).map((m) => ({ ...m }));
      const lb = (currentTournament.losersMatches || []).map((m) => ({ ...m }));
      const gf = currentTournament.grandFinal ? { ...currentTournament.grandFinal } : null;
      const gfr = currentTournament.grandFinalReset ? { ...currentTournament.grandFinalReset } : null;
      const target =
        wb.find((m) => m.id === matchId) ||
        lb.find((m) => m.id === matchId) ||
        (gf && gf.id === matchId ? gf : null) ||
        (gfr && gfr.id === matchId ? gfr : null);
      if (!target) return;
      const winner = scoreA > scoreB ? target.participantA : target.participantB;
      target.scoreA = scoreA; target.scoreB = scoreB; target.winner = winner; target.status = 'completed';
      const out = recomputeDoubleElimination(wb, lb, gf, gfr);
      const dePayload = {
        matches: out.matches,
        losersMatches: out.losersMatches,
        grandFinal: out.grandFinal,
        grandFinalReset: out.grandFinalReset,
        champion: out.champion,
        status: out.status,
        playerStats: mergedStats,
      };
      try {
        const response = await fetch(`${API_URL}/api/tournaments/${currentTournament.id}?creator_token=${creatorToken}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
          body: JSON.stringify(dePayload),
        });
        if (response.ok) setCurrentTournament(await response.json());
      } catch (error) {
        console.error('Error updating score:', error);
      }
      return;
    }

    const isGrandFinal = bracketType === 'grandFinal';
    const isLosers = bracketType === 'losers';
    const isBronze = bracketType === 'bronze';
    
    let updatePayload = {};
    let champion = null;
    let status = 'active';
    
    // Handle Bronze Medal Match
    if (isBronze) {
      const winner = scoreA > scoreB ? currentTournament.bronzeMatch.participantA : currentTournament.bronzeMatch.participantB;
      const loser = scoreA > scoreB ? currentTournament.bronzeMatch.participantB : currentTournament.bronzeMatch.participantA;
      
      const updatedBronzeMatch = {
        ...currentTournament.bronzeMatch,
        scoreA,
        scoreB,
        winner,
        loser,
        status: 'completed',
      };
      
      updatePayload = {
        bronzeMatch: updatedBronzeMatch,
      };
    }
    // Handle Grand Final match
    else if (isGrandFinal) {
      const winner = scoreA > scoreB ? currentTournament.grandFinal.participantA : currentTournament.grandFinal.participantB;
      const loser = scoreA > scoreB ? currentTournament.grandFinal.participantB : currentTournament.grandFinal.participantA;
      
      const updatedGrandFinal = {
        ...currentTournament.grandFinal,
        scoreA,
        scoreB,
        winner,
        loser,
        status: 'completed',
      };
      
      // In Double Elimination, if losers bracket champion wins Grand Final, there's a reset match
      // For simplicity, we'll just declare the winner as champion
      champion = winner;
      status = 'completed';
      
      updatePayload = {
        grandFinal: updatedGrandFinal,
        champion,
        status,
      };
    } else {
      // Handle Winners or Losers bracket
      // Build working copies and roll back any PREVIOUS effects of this match
      // (handles re-editing a completed score, not just first-time entry).
      const winners = (currentTournament.matches || []).map(m => ({ ...m }));
      const losers = (currentTournament.losersMatches || []).map(m => ({ ...m }));
      const bronzeMatch = currentTournament.bronzeMatch ? { ...currentTournament.bronzeMatch } : null;
      let grandFinal = currentTournament.grandFinal ? { ...currentTournament.grandFinal } : null;

      rollbackMatchEffects(winners, losers, bronzeMatch, grandFinal, matchId, bracketType);

      const matchArr = isLosers ? losers : winners;
      const completedMatch = matchArr.find(m => m.id === matchId);
      if (!completedMatch) return;

      // Apply the new result to the target match
      const winner = scoreA > scoreB ? completedMatch.participantA : completedMatch.participantB;
      const loser = scoreA > scoreB ? completedMatch.participantB : completedMatch.participantA;
      completedMatch.scoreA = scoreA;
      completedMatch.scoreB = scoreB;
      completedMatch.winner = winner;
      completedMatch.loser = loser;
      completedMatch.status = 'completed';

      // Advance winner within their own bracket
      if (winner && completedMatch.nextMatchIndex !== null && completedMatch.nextMatchIndex !== undefined) {
        const nextMatch = matchArr.filter(m => m.round === completedMatch.round + 1)[completedMatch.nextMatchIndex];
        if (nextMatch) {
          const isFirst = completedMatch.matchIndex % 2 === 0;
          if (isFirst) nextMatch.participantA = winner;
          else nextMatch.participantB = winner;
          if (nextMatch.participantA && nextMatch.participantB) nextMatch.status = 'pending';
        }
      }

      updatePayload[isLosers ? 'losersMatches' : 'matches'] = matchArr;

      // Handle Double Elimination specific logic
      if (currentTournament.hasLosersLeague) {
        // Winners bracket match completed -> drop loser into the losers bracket
        if (!isLosers && loser && !loser.isBye) {
          const isWinnersFinal = completedMatch.roundName === 'Winners Final';
          if (isWinnersFinal) {
            const losersFinal = losers.find(m => m.roundName === 'Losers Final');
            if (losersFinal) {
              losersFinal.participantB = loser;
              if (losersFinal.participantA) losersFinal.status = 'pending';
            }
            if (grandFinal) {
              grandFinal.participantA = winner;
              if (grandFinal.participantB) grandFinal.status = 'pending';
            }
          } else {
            let targetLosersRound = 0;
            if (completedMatch.round === 0) targetLosersRound = 0;
            else if (completedMatch.round === 1) targetLosersRound = 1;
            else targetLosersRound = completedMatch.round;

            const targetRoundMatches = losers.filter(m => m.round === targetLosersRound);
            for (let lm of targetRoundMatches) {
              if (!lm.participantA) {
                lm.participantA = loser;
                if (lm.participantB) lm.status = 'pending';
                break;
              } else if (!lm.participantB) {
                lm.participantB = loser;
                if (lm.participantA) lm.status = 'pending';
                break;
              }
            }
          }
          updatePayload.losersMatches = losers;
        }

        // Losers bracket match completed -> advance Losers Final winner to Grand Final
        if (isLosers) {
          const losersFinalMatch = losers.find(m => m.roundName === 'Losers Final');
          if (losersFinalMatch?.status === 'completed' && losersFinalMatch.winner) {
            if (grandFinal) {
              grandFinal.participantB = losersFinalMatch.winner;
              if (grandFinal.participantA) grandFinal.status = 'pending';
            }
          }
          updatePayload.losersMatches = losers;
        }

        if (grandFinal) updatePayload.grandFinal = grandFinal;

        // Champion only decided at the Grand Final (handled in its own branch);
        // clear it here in case an upstream edit invalidated it.
        if (grandFinal?.status === 'completed' && grandFinal.winner) {
          updatePayload.champion = grandFinal.winner;
          updatePayload.status = 'completed';
        } else {
          updatePayload.champion = null;
          updatePayload.status = 'active';
        }
      } else if (currentTournament.hasBronzeMatch) {
        // Single Elimination with Bronze Match
        if (loser && !loser.isBye && completedMatch.roundName?.includes('Semi') && bronzeMatch) {
          if (!bronzeMatch.participantA) bronzeMatch.participantA = loser;
          else if (!bronzeMatch.participantB) bronzeMatch.participantB = loser;
          if (bronzeMatch.participantA && bronzeMatch.participantB) bronzeMatch.status = 'pending';
        }
        if (bronzeMatch) updatePayload.bronzeMatch = bronzeMatch;

        const finalMatch = winners.find(m => m.roundName?.includes('Final') && !m.roundName?.includes('Semi'));
        if (finalMatch?.status === 'completed') {
          champion = finalMatch.winner;
          const bronzeCompleted = !bronzeMatch || bronzeMatch.status === 'completed' || !bronzeMatch.participantA;
          status = bronzeCompleted ? 'completed' : 'active';
          updatePayload.champion = champion;
          updatePayload.status = status;
        } else {
          updatePayload.champion = null;
          updatePayload.status = 'active';
        }
      } else {
        // Single Elimination - check for champion
        const finalMatch = winners.find(m => m.roundName?.includes('Final'));
        if (finalMatch?.status === 'completed') {
          updatePayload.champion = finalMatch.winner;
          updatePayload.status = 'completed';
        } else {
          updatePayload.champion = null;
          updatePayload.status = 'active';
        }
      }
    }

    updatePayload.playerStats = mergedStats;

    try {
      const response = await fetch(`${API_URL}/api/tournaments/${currentTournament.id}?creator_token=${creatorToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updatePayload),
      });

      if (response.ok) {
        const updated = await response.json();
        setCurrentTournament(updated);
      }
    } catch (error) {
      console.error('Error updating score:', error);
    }
  }, [currentTournament, isCreator]);

  // Update league score
  const updateLeagueScore = useCallback(async (matchId, scoreA, scoreB, matchPlayerStats = null) => {
    if (!currentTournament || !isCreator) return;

    const creatorToken = getCreatorToken(currentTournament.id);

    const mergedStats = { ...(currentTournament.playerStats || {}) };
    if (matchPlayerStats) mergedStats[matchId] = matchPlayerStats;

    const updatedMatches = currentTournament.matches.map(m => {
      if (m.id !== matchId) return m;
      const winner = scoreA > scoreB ? m.participantA : scoreB > scoreA ? m.participantB : null;
      return { ...m, scoreA, scoreB, winner, status: 'completed' };
    });

    const standings = calculateStandings(currentTournament.participants, updatedMatches);
    const allCompleted = updatedMatches.every(m => m.status === 'completed');
    const champion = allCompleted ? standings[0] : null;
    const status = allCompleted ? 'completed' : 'active';

    try {
      const response = await fetch(`${API_URL}/api/tournaments/${currentTournament.id}?creator_token=${creatorToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ matches: updatedMatches, standings, champion, status, playerStats: mergedStats }),
      });

      if (response.ok) {
        const updated = await response.json();
        setCurrentTournament(updated);
      }
    } catch (error) {
      console.error('Error updating score:', error);
    }
  }, [currentTournament, isCreator]);

  // Update match schedule
  const updateMatchSchedule = useCallback(async (matchId, scheduleData) => {
    if (!currentTournament || !isCreator) return;

    const creatorToken = getCreatorToken(currentTournament.id);

    const updatedMatches = currentTournament.matches.map(m => {
      if (m.id !== matchId) return m;
      return {
        ...m,
        scheduledDate: scheduleData.scheduledDate || '',
        scheduledTime: scheduleData.scheduledTime || '',
        location: scheduleData.location || '',
        notes: scheduleData.notes || '',
      };
    });

    try {
      const response = await fetch(`${API_URL}/api/tournaments/${currentTournament.id}?creator_token=${creatorToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ matches: updatedMatches }),
      });

      if (response.ok) {
        const updated = await response.json();
        setCurrentTournament(updated);
      }
    } catch (error) {
      console.error('Error updating schedule:', error);
    }
  }, [currentTournament, isCreator]);

  // Reset match
  const resetMatch = useCallback(async (matchId, bracketType = 'winners') => {
    if (!currentTournament || !isCreator) return;

    const creatorToken = getCreatorToken(currentTournament.id);

    // Drop this match's player stats when its result is cleared.
    const clearedStats = { ...(currentTournament.playerStats || {}) };
    delete clearedStats[matchId];

    // Double elimination: clear the target result and recompute everything.
    if (currentTournament.hasLosersLeague) {
      const wb = (currentTournament.matches || []).map((m) => ({ ...m }));
      const lb = (currentTournament.losersMatches || []).map((m) => ({ ...m }));
      const gf = currentTournament.grandFinal ? { ...currentTournament.grandFinal } : null;
      const gfr = currentTournament.grandFinalReset ? { ...currentTournament.grandFinalReset } : null;
      const target =
        wb.find((m) => m.id === matchId) ||
        lb.find((m) => m.id === matchId) ||
        (gf && gf.id === matchId ? gf : null) ||
        (gfr && gfr.id === matchId ? gfr : null);
      if (target) {
        target.scoreA = null; target.scoreB = null; target.winner = null; target.loser = null;
      }
      const out = recomputeDoubleElimination(wb, lb, gf, gfr);
      const dePayload = {
        matches: out.matches,
        losersMatches: out.losersMatches,
        grandFinal: out.grandFinal,
        grandFinalReset: out.grandFinalReset,
        champion: out.champion,
        status: out.status,
        playerStats: clearedStats,
      };
      try {
        const response = await fetch(`${API_URL}/api/tournaments/${currentTournament.id}?creator_token=${creatorToken}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
          body: JSON.stringify(dePayload),
        });
        if (response.ok) setCurrentTournament(await response.json());
      } catch (error) {
        console.error('Error resetting match:', error);
      }
      return;
    }

    let payload;

    if (currentTournament.format === 'league') {
      const updatedMatches = currentTournament.matches.map(m => {
        if (m.id !== matchId) return m;
        return { ...m, scoreA: null, scoreB: null, winner: null, status: 'pending' };
      });
      const standings = calculateStandings(currentTournament.participants, updatedMatches);
      payload = { matches: updatedMatches, standings, champion: null, status: 'active', playerStats: clearedStats };
    } else {
      // Elimination: roll back this match AND every downstream effect it caused
      // (advanced teams, losers-bracket drops, bronze-match placement, grand final).
      const winners = (currentTournament.matches || []).map(m => ({ ...m }));
      const losers = (currentTournament.losersMatches || []).map(m => ({ ...m }));
      const bronzeMatch = currentTournament.bronzeMatch ? { ...currentTournament.bronzeMatch } : null;
      const grandFinal = currentTournament.grandFinal ? { ...currentTournament.grandFinal } : null;

      rollbackMatchEffects(winners, losers, bronzeMatch, grandFinal, matchId, bracketType);

      payload = { matches: winners, champion: null, status: 'active', playerStats: clearedStats };
      if (currentTournament.losersMatches) payload.losersMatches = losers;
      if (currentTournament.bronzeMatch) payload.bronzeMatch = bronzeMatch;
      if (currentTournament.grandFinal) payload.grandFinal = grandFinal;
    }

    try {
      const response = await fetch(`${API_URL}/api/tournaments/${currentTournament.id}?creator_token=${creatorToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const updated = await response.json();
        setCurrentTournament(updated);
      }
    } catch (error) {
      console.error('Error resetting match:', error);
    }
  }, [currentTournament, isCreator]);

  // Delete tournament
  const deleteTournament = useCallback(async (id) => {
    const creatorToken = getCreatorToken(id);
    
    try {
      await fetch(`${API_URL}/api/tournaments/${id}?creator_token=${creatorToken}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      setTournaments(prev => prev.filter(t => t.id !== id));
      if (currentTournament?.id === id) setCurrentTournament(null);
    } catch (error) {
      console.error('Error deleting tournament:', error);
    }
  }, [currentTournament]);

  // Load the signed-in user's tournaments (account-based). Re-runs when auth changes.
  // Load the signed-in user's tournaments (account-based). Re-runs when auth changes.
  useEffect(() => {
    const loadMyTournaments = async () => {
      if (!isAuthenticated) {
        setTournaments([]);
        return;
      }      try {
        const response = await fetch(`${API_URL}/api/tournaments/mine`, { credentials: 'include' });
        if (response.ok) {
          const mine = await response.json();
          setTournaments(mine);
        }
      } catch (error) {
        console.error('Error loading your tournaments:', error);
      }
    };

    loadMyTournaments();
  }, [isAuthenticated]);

  return (
    <TournamentContext.Provider
      value={{
        tournaments,
        currentTournament,
        isCreator,
        loading,
        createTournament,
        editTournament,
        updateBracketScore,
        updateLeagueScore,
        updateMatchSchedule,
        loadTournament,
        deleteTournament,
        findTournamentByCode,
        resetMatch,
        setCurrentTournament,
      }}
    >
      {children}
    </TournamentContext.Provider>
  );
}

export const useTournament = () => {
  const context = useContext(TournamentContext);
  if (!context) throw new Error('useTournament must be used within TournamentProvider');
  return context;
};
